import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, ShieldCheck, X } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useStore } from "../context/StoreContext";

const inputCls =
  "w-full rounded-xl border border-brand-brown/15 bg-white px-4 py-3 text-sm text-brand-ink outline-none transition-colors focus:border-brand-gold";

export const CustomerAuthModal = () => {
  const { authOpen, setAuthOpen, loginCustomer } = useStore();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [needName, setNeedName] = useState(false);
  const [devOtp, setDevOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const close = () => {
    setAuthOpen(false);
    setStep("phone");
    setOtp("");
    setError("");
    setNeedName(false);
  };

  const sendOtp = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/customer/request-otp", { phone });
      setDevOtp(data.dev_mode ? data.dev_otp : "");
      setStep("otp");
    } catch (e2) {
      setError(errMsg(e2));
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/customer/verify-otp", {
        phone, otp, name: name.trim() || undefined,
      });
      if (data.need_name) {
        setNeedName(true);
        setError("Welcome! Please tell us your name to finish signing up.");
        return;
      }
      loginCustomer(data.token, data.customer);
      close();
    } catch (e2) {
      setError(errMsg(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {authOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[95] flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center sm:p-4"
          onClick={close}
        >
          <motion.div
            data-testid="auth-modal"
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm rounded-t-3xl bg-brand-cream p-7 shadow-2xl sm:rounded-3xl"
          >
            <div className="flex items-start justify-between">
              <div>
                <h2 className="font-serif text-2xl font-bold text-brand-ink">
                  {step === "phone" ? "Sign In" : "Enter OTP"}
                </h2>
                <p className="mt-1 text-xs text-brand-mute">
                  {step === "phone"
                    ? "Sign in with your mobile number to order."
                    : `Sent to +91 ${phone}`}
                </p>
              </div>
              <button onClick={close} className="rounded-full p-2 hover:bg-brand-beige" aria-label="Close" data-testid="auth-close-button">
                <X className="h-5 w-5 text-brand-brown" />
              </button>
            </div>

            {step === "phone" ? (
              <form onSubmit={sendOtp} className="mt-6 space-y-4">
                <label className="block">
                  <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.16em] text-brand-mute">Mobile Number</span>
                  <div className="flex items-center gap-2">
                    <span className="rounded-xl border border-brand-brown/15 bg-white px-3.5 py-3 text-sm font-semibold text-brand-ink">+91</span>
                    <input
                      data-testid="auth-phone-input"
                      inputMode="numeric"
                      maxLength={10}
                      required
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                      className={inputCls}
                      placeholder="10-digit mobile"
                    />
                  </div>
                </label>
                {error && <p className="rounded-xl bg-brand-red/10 px-4 py-3 text-sm font-semibold text-brand-red" data-testid="auth-error">{error}</p>}
                <button
                  data-testid="auth-send-otp-button"
                  disabled={loading || phone.length !== 10}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-brand-red py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-brand-cream shadow-lg shadow-brand-red/25 disabled:opacity-50"
                >
                  <Phone className="h-4 w-4" /> {loading ? "Sending…" : "Send OTP"}
                </button>
              </form>
            ) : (
              <form onSubmit={verify} className="mt-6 space-y-4">
                {devOtp && (
                  <p className="rounded-xl bg-brand-amber/15 px-4 py-3 text-center text-sm font-bold text-brand-brown" data-testid="auth-dev-otp">
                    Test Mode — your OTP is <span className="font-serif text-lg tracking-[0.2em]">{devOtp}</span>
                  </p>
                )}
                <label className="block">
                  <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.16em] text-brand-mute">4-digit OTP</span>
                  <input
                    data-testid="auth-otp-input"
                    inputMode="numeric"
                    maxLength={4}
                    required
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    className={`${inputCls} text-center font-serif text-2xl tracking-[0.5em]`}
                    placeholder="••••"
                  />
                </label>
                {needName && (
                  <label className="block">
                    <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.16em] text-brand-mute">Your Name</span>
                    <input
                      data-testid="auth-name-input"
                      required
                      minLength={2}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className={inputCls}
                      placeholder="e.g. Sanjay"
                    />
                  </label>
                )}
                {error && <p className="rounded-xl bg-brand-red/10 px-4 py-3 text-sm font-semibold text-brand-red" data-testid="auth-error">{error}</p>}
                <button
                  data-testid="auth-verify-button"
                  disabled={loading || otp.length !== 4}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-brand-red py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-brand-cream shadow-lg shadow-brand-red/25 disabled:opacity-50"
                >
                  <ShieldCheck className="h-4 w-4" /> {loading ? "Verifying…" : "Verify & Sign In"}
                </button>
                <button
                  type="button"
                  onClick={() => { setStep("phone"); setOtp(""); setError(""); }}
                  className="w-full text-center text-xs font-semibold text-brand-mute hover:text-brand-red"
                >
                  Change mobile number
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
